import React, { useEffect } from 'react';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  SimpleGrid,
  Switch,
  FormControl,
  FormLabel,
  HStack,
} from '@chakra-ui/react';
import { toast } from 'sonner';
import { InputForm } from '../../../../../../components/inputs/forms/InputForm';
import { TextAreaInputForm } from '../../../../../../components/inputs/forms/TextAreaInputForm';
import { useIcms } from '../../../../../../hooks/useIcms';
import {
  IAcumulador,
  IAcumuladorPayload,
  ClassificacaoAcumulador,
  TipoOperacaoAcumulador,
} from '../../../../../../types/icms';

interface AcumuladorFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  codiEmp: number;
  acumulador: IAcumulador | null;
}

interface FormValues {
  codi_acu: string;
  nome_acu: string;
  tipo_operacao: TipoOperacaoAcumulador;
  classificacao: ClassificacaoAcumulador;
  cfops_padrao_str: string;
  gera_credito: boolean;
  gera_debito: boolean;
  observacoes: string;
}

const schema = yup.object({
  codi_acu: yup.string().required('Código obrigatório'),
  nome_acu: yup.string().required('Nome obrigatório'),
  tipo_operacao: yup.mixed<TipoOperacaoAcumulador>().oneOf(['entrada', 'saida']).required(),
  classificacao: yup.mixed<ClassificacaoAcumulador>().required(),
  cfops_padrao_str: yup.string(),
  gera_credito: yup.boolean(),
  gera_debito: yup.boolean(),
  observacoes: yup.string(),
});

export function AcumuladorFormModal({
  isOpen,
  onClose,
  codiEmp,
  acumulador,
}: AcumuladorFormModalProps) {
  const queryClient = useQueryClient();
  const { upsertAcumulador, updateAcumulador, getClassificacoesAcumulador } = useIcms();

  const isEdit = !!acumulador;

  const methods = useForm<FormValues>({
    resolver: yupResolver<FormValues>(schema as yup.ObjectSchema<FormValues>),
    defaultValues: {
      codi_acu: '',
      nome_acu: '',
      tipo_operacao: 'saida',
      classificacao: 'outras',
      cfops_padrao_str: '',
      gera_credito: false,
      gera_debito: false,
      observacoes: '',
    },
  });

  const { data: classificacoes } = useQuery({
    queryKey: ['icms-classificacoes-acumulador'],
    queryFn: getClassificacoesAcumulador,
    staleTime: Infinity,
    enabled: isOpen,
  });

  useEffect(() => {
    if (isOpen) {
      methods.reset({
        codi_acu: acumulador?.codi_acu ?? '',
        nome_acu: acumulador?.nome_acu ?? '',
        tipo_operacao: acumulador?.tipo_operacao ?? 'saida',
        classificacao: acumulador?.classificacao ?? 'outras',
        cfops_padrao_str: (acumulador?.cfops_padrao ?? []).join(', '),
        gera_credito: acumulador?.gera_credito ?? false,
        gera_debito: acumulador?.gera_debito ?? false,
        observacoes: acumulador?.observacoes ?? '',
      });
    }
  }, [isOpen, acumulador, methods]);

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const cfops = values.cfops_padrao_str
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);
      const payload: IAcumuladorPayload = {
        codi_acu: values.codi_acu,
        nome_acu: values.nome_acu,
        tipo_operacao: values.tipo_operacao,
        classificacao: values.classificacao,
        cfops_padrao: cfops.length > 0 ? cfops : null,
        gera_credito: values.gera_credito,
        gera_debito: values.gera_debito,
        observacoes: values.observacoes || null,
      };
      if (isEdit && acumulador) {
        return updateAcumulador(codiEmp, acumulador.codi_acu, payload);
      }
      return upsertAcumulador(codiEmp, payload);
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Acumulador atualizado' : 'Acumulador criado');
      queryClient.invalidateQueries({ queryKey: ['acumuladores'] });
      onClose();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || 'Erro ao salvar. Tente novamente.');
    },
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{isEdit ? 'Editar acumulador' : 'Novo acumulador'}</ModalHeader>
        <ModalCloseButton />
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(values => mutation.mutate(values))}>
            <ModalBody>
              <SimpleGrid columns={2} spacing={3}>
                <InputForm name="codi_acu" label="Código" isRequired isReadOnly={isEdit} />
                <InputForm name="nome_acu" label="Nome" isRequired />
                <FormControl>
                  <FormLabel fontWeight="bold" mb={1}>
                    Tipo de operação
                  </FormLabel>
                  <select
                    {...methods.register('tipo_operacao')}
                    style={{
                      width: '100%',
                      height: '40px',
                      borderRadius: '6px',
                      border: '1px solid #E2E8F0',
                      padding: '0 12px',
                      background: '#fff',
                    }}
                  >
                    <option value="entrada">Entrada</option>
                    <option value="saida">Saída</option>
                  </select>
                </FormControl>
                <FormControl>
                  <FormLabel fontWeight="bold" mb={1}>
                    Classificação
                  </FormLabel>
                  <select
                    {...methods.register('classificacao')}
                    style={{
                      width: '100%',
                      height: '40px',
                      borderRadius: '6px',
                      border: '1px solid #E2E8F0',
                      padding: '0 12px',
                      background: '#fff',
                    }}
                  >
                    {(classificacoes ?? []).map(c => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </FormControl>
              </SimpleGrid>
              <InputForm
                name="cfops_padrao_str"
                label="CFOPs (separados por vírgula)"
                placeholder="5102, 5405"
                helper="Lista de CFOPs típicos desse acumulador"
              />
              <HStack spacing={6} mb={3}>
                <Controller
                  name="gera_credito"
                  control={methods.control}
                  render={({ field }) => (
                    <FormControl display="flex" alignItems="center">
                      <FormLabel htmlFor="gera_credito" fontWeight="bold" mb={0} mr={2}>
                        Gera crédito
                      </FormLabel>
                      <Switch
                        id="gera_credito"
                        isChecked={field.value}
                        onChange={e => field.onChange(e.target.checked)}
                      />
                    </FormControl>
                  )}
                />
                <Controller
                  name="gera_debito"
                  control={methods.control}
                  render={({ field }) => (
                    <FormControl display="flex" alignItems="center">
                      <FormLabel htmlFor="gera_debito" fontWeight="bold" mb={0} mr={2}>
                        Gera débito
                      </FormLabel>
                      <Switch
                        id="gera_debito"
                        isChecked={field.value}
                        onChange={e => field.onChange(e.target.checked)}
                      />
                    </FormControl>
                  )}
                />
              </HStack>
              <TextAreaInputForm name="observacoes" label="Observações" />
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose}>
                Cancelar
              </Button>
              <Button colorScheme="blue" type="submit" isLoading={mutation.isPending}>
                Salvar
              </Button>
            </ModalFooter>
          </form>
        </FormProvider>
      </ModalContent>
    </Modal>
  );
}
